// ManageRegistrationsV2.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Grid,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { debounce, startCase, toLower } from "lodash";
import * as XLSX from "xlsx";

import { FaFileDownload, FaFileExcel, FaPen } from "react-icons/fa";
import {
  DownloadOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { db } from "../../firebaseConfig";
import { generateInvoicePdf } from "../../util/generateInvoicePdf";
import { toBase64 } from "../OrdersV4";
import CustomCountrySelect from "../CustomCountrySelect";
import PhoneInput from "react-phone-number-input";
import "./registration.css";

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// -------------------- Types --------------------
export type Participants = {
  docId?: string;

  // legacy
  name: string;

  title?: string;
  fname: string;
  lname: string;
  email: string;
  organization: string;
  position: string;
  mobileNumber: string;

  ticketType: string;
  nationality?: string;

  companionName?: string;
  companionRelationship?: string;
  country?: string;

  status?: string;
  notes?: string;

  referralSource?: string;
  referralOther?: string;

  badgeCategory?: string;
  badgeCollected?: string;
};

type Registration = {
  key: string;
  totalAmount: string;
  totalDelegateTickets: string;
  totalUaefmaTickets: string;
  coupon: string;
  timestamp: string;
  contactPersons: Participants[];
  invoiceNumber?: string;
  ticketId?: string;
};

interface ParticipantsWithOrder extends Participants {
  registrationKey: string;
  timestamp: string;
  invoiceNumber?: string;
}

// -------------------- UI Tokens --------------------
const glassCard: React.CSSProperties = {
  borderRadius: 18,
  border: "1px solid rgba(15, 23, 42, 0.06)",
  background: "rgba(255,255,255,0.85)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
};

const controlStyle: React.CSSProperties = {
  height: 44,
  borderRadius: 14,
};

const statusToTagColor = (status?: string) => {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "green";
    case "pending payment":
      return "gold";
    case "payment not reflected":
      return "red";
    case "complimentary pass":
      return "blue";
    case "manual entry":
      return "orange";
    default:
      return "default";
  }
};

// -------------------- Pricing / Coupon Helpers --------------------
type Coupon = {
  key: string;
  code: string;
  discount: number;
  discountType: "percentage" | "exact";
  active: boolean;
};

const BASE_FEE_BY_TICKET_ID: Record<string, number> = {
  delegate: 1950,
  uaefma: 950,
  exhibitor: 1950, // adjust if your exhibitor base price differs
};

const ticketIdFrom = (ticketId?: string, ticketLabel?: string) => {
  if (ticketId) return ticketId;
  const label = (ticketLabel || "").toLowerCase();
  if (label.includes("uaefma")) return "uaefma";
  if (label.includes("exhibitor")) return "exhibitor";
  if (label.includes("delegate")) return "delegate";
  return "";
};

const getBaseFeeByTicketId = (id: string) => BASE_FEE_BY_TICKET_ID[id] ?? null;

const toTitleCase = (str: string = "") => startCase(toLower(str));

const ManageRegistrationsV2: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Participants[]>([]);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
  const [currentContacts, setCurrentContacts] = useState<Participants[]>([]);
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingContact, setEditingContact] =
    useState<ParticipantsWithOrder | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const [couponMap, setCouponMap] = useState<Record<string, Coupon>>({});

  // 🟧 Add manual registration modal
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newContact, setNewContact] = useState<Partial<Participants>>({
    fname: "",
    lname: "",
    email: "",
    organization: "",
    position: "",
    mobileNumber: "",
    country: "",
    nationality: "",
    companionName: "",
    companionRelationship: "",
    ticketType: "Delegate",
    badgeCategory: "Delegate",
    title: "",
  });

  // -------------------- Auth guard --------------------
  useEffect(() => {
    if (!localStorage.getItem("isAuthenticated")) navigate("/login");
  }, [navigate]);

  // -------------------- Helpers --------------------
  const getFullName = (contact: Partial<Participants>) =>
    [contact.title, contact.fname, contact.lname].filter(Boolean).join(" ") ||
    contact.name ||
    "";

  const handleSearch = (value: string) => {
    setSearchTerm(value.toLowerCase());
  };

  // -------------------- Firestore: update field --------------------
  const updateContactField = async (
    orderId: string,
    contactId: string,
    field: string,
    value: any,
  ) => {
    try {
      const contactRef = doc(db, `orders/${orderId}/contacts`, contactId);
      await updateDoc(contactRef, { [field]: value });

      // Toast only for status/notes
      if (field === "status" || field === "notes") {
        message.success(`${field === "status" ? "Status" : "Notes"} updated.`);
      }

      // Local state update
      setRegistrations((prevRegs) =>
        prevRegs.map((reg) => {
          if (reg.key !== orderId) return reg;
          const updatedContacts = reg.contactPersons.map((c) =>
            c.docId === contactId ? { ...c, [field]: value } : c,
          );
          return { ...reg, contactPersons: updatedContacts };
        }),
      );
    } catch (err) {
      console.error("Error updating contact field:", err);
      message.error("Failed to update contact field.");
    }
  };

  // -------------------- Debounced updates (stable + safe cleanup) --------------------
  const debounceUpdateNotes = useMemo(
    () =>
      debounce((orderId: string, contactId: string, value: string) => {
        updateContactField(orderId, contactId, "notes", value);
      }, 500),
    [],
  );

  const debounceUpdateStatus = useMemo(
    () =>
      debounce((orderId: string, contactId: string, value: string) => {
        updateContactField(orderId, contactId, "status", value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceUpdateNotes.cancel();
      debounceUpdateStatus.cancel();
    };
  }, [debounceUpdateNotes, debounceUpdateStatus]);

  // -------------------- Fetch coupons once --------------------
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "coupons"));
        const map: Record<string, Coupon> = {};
        snap.docs.forEach((d) => {
          const data = d.data() as any;
          const codeKey = String(data.code || "")
            .trim()
            .toLowerCase();
          if (!codeKey) return;
          map[codeKey] = {
            key: d.id,
            code: String(data.code || ""),
            discount: Number(data.discount) || 0,
            discountType:
              data.discountType === "exact" ? "exact" : "percentage",
            active: !!data.active,
          };
        });
        setCouponMap(map);
      } catch (e) {
        console.error("Failed to fetch coupons:", e);
      }
    })();
  }, []);

  // -------------------- Fetch registrations --------------------
  const fetchRegistrations = async () => {
    try {
      const ordersRef = collection(db, "orders");
      const q = query(ordersRef, orderBy("timestamp", "desc"));
      const orderSnapshot = await getDocs(q);

      const ordersWithContacts: Registration[] = await Promise.all(
        orderSnapshot.docs.map(async (orderDoc) => {
          const orderData = orderDoc.data() as any;
          const orderId = orderDoc.id;

          const timestamp = orderData.timestamp?.toDate
            ? orderData.timestamp.toDate().toLocaleString()
            : "N/A";

          const contactsRef = collection(db, `orders/${orderId}/contacts`);
          const contactsSnapshot = await getDocs(contactsRef);

          const contactPersons: Participants[] = contactsSnapshot.empty
            ? [
                {
                  docId: "",
                  name: "N/A",
                  fname: "N/A",
                  lname: "N/A",
                  email: "N/A",
                  organization: "N/A",
                  position: "N/A",
                  mobileNumber: "N/A",
                  ticketType: "N/A",
                  status: "Pending",
                  notes: "",
                },
              ]
            : contactsSnapshot.docs.map((contactDoc) => {
                const data = contactDoc.data() as any;
                const companion = data.companion || {};

                const ticketType =
                  data.ticketId === "delegate"
                    ? "Delegate"
                    : data.ticketId === "uaefma"
                      ? "UAEFMA Member"
                      : data.ticketId === "exhibitor"
                        ? "Exhibitor"
                        : "Unknown";

                return {
                  docId: contactDoc.id,
                  name: getFullName(data),
                  fname: data.fname || "",
                  lname: data.lname || "",
                  email: data.email || "N/A",
                  organization: data.organization || "N/A",
                  position: data.position || "N/A",
                  mobileNumber: data.mobileNumber || "N/A",
                  ticketType,
                  badgeCategory: data.badgeCategory || "",
                  badgeCollected: data.badgeCollected || "No",
                  status: data.status || "Awaiting Payment Confirmation",
                  notes: data.notes || "",
                  country: data.country || "N/A",
                  nationality: data.nationality || "N/A",
                  title: data.title || "",
                  companionName: companion.fullName || "",
                  companionRelationship: companion.relationship || "",
                  referralSource: data.referralSource || "",
                  referralOther: data.referralOther || "",
                };
              });

          return {
            key: orderId,
            totalAmount: orderData.totalAmount || "0.00",
            totalDelegateTickets: orderData.totalDelegateTickets || "0",
            totalUaefmaTickets: orderData.totalUaefmaTickets || "0",
            coupon: orderData.coupon || "None",
            timestamp,
            contactPersons,
            invoiceNumber: orderData.invoiceNumber,
            ticketId: orderData.ticketId || "N/A",
          };
        }),
      );

      setRegistrations(ordersWithContacts);
    } catch (error) {
      console.error("Error fetching registrations:", error);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    const intervalId = setInterval(fetchRegistrations, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // -------------------- Print modal --------------------
  const handlePrintBadges = (record: Registration) => {
    setSelectedRegistration(record);
    setCurrentContacts(record.contactPersons);
    setSelectedContacts(record.contactPersons);
    setIsPrintModalVisible(true);
  };

  const handlePrintSelected = () => {
    setIsPrintModalVisible(false);
    message.success(`Printing ${selectedContacts.length} badges...`);
  };

  const modalColumns = [
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: any, contact: Participants) => (
        <Checkbox
          checked={selectedContacts.some((c) => c.email === contact.email)}
          onChange={() => {
            setSelectedContacts((prev) => {
              const alreadySelected = prev.some(
                (c) => c.email === contact.email,
              );
              return alreadySelected
                ? prev.filter((c) => c.email !== contact.email)
                : [...prev, contact];
            });
          }}
        />
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (_: any, contact: Participants) =>
        contact.title && contact.fname && contact.lname
          ? `${contact.title} ${contact.fname} ${contact.lname}`
          : contact.fname || contact.lname || contact.name || "N/A",
    },
  ];

  // -------------------- Discount helper (used by resend) --------------------
  const applyDiscount = async (
    total: number,
    couponCode: string,
  ): Promise<{
    discountedTotal: number;
    discountAmount: number;
    discountText: string;
  }> => {
    if (!couponCode || couponCode === "None") {
      return {
        discountedTotal: total,
        discountAmount: 0,
        discountText: "None",
      };
    }

    const couponsRef = collection(db, "coupons");
    const q = query(couponsRef, where("code", "==", couponCode.trim()));
    const snap = await getDocs(q);

    if (snap.empty) {
      return {
        discountedTotal: total,
        discountAmount: 0,
        discountText: "Invalid or Inactive Coupon",
      };
    }

    const coupon = snap.docs[0].data() as any;
    if (!coupon.active) {
      return {
        discountedTotal: total,
        discountAmount: 0,
        discountText: "Inactive Coupon",
      };
    }

    const { discount, discountType } = coupon;

    let discountedTotal = total;
    let discountAmount = 0;
    let discountText = "";

    if (discountType === "percentage") {
      discountAmount = total * (Number(discount) / 100);
      discountedTotal = total - discountAmount;
      discountText = `${discount}% off`;
    } else if (discountType === "fixed" || discountType === "exact") {
      // treat fixed/exact as flat amount OFF here
      discountAmount = Number(discount) || 0;
      discountedTotal = Math.max(0, total - discountAmount);
      discountText = `${discount} AED off`;
    }

    const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

    return {
      discountedTotal: round2(discountedTotal),
      discountAmount: round2(discountAmount),
      discountText,
    };
  };

  // -------------------- Resend email (kept same logic) --------------------
  const handleResendRegistrationEmail = async (
    contact: ParticipantsWithOrder,
  ) => {
    const allEmails = [
      contact.email,
      "webmastercreativespace@gmail.com",
      "relhage@ica-arab.com",
      "eman@uaefma.com",
      "juna.m@uaefma.com",
      "saquib@uaefma.com",
    ];

    let selected: string[] = [...allEmails];

    Modal.confirm({
      title: "Resend Registration Email",
      content: (
        <Checkbox.Group
          defaultValue={allEmails}
          onChange={(vals) => {
            selected = vals as string[];
          }}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {allEmails.map((email) => (
            <Checkbox key={email} value={email}>
              {email === contact.email ? (
                <span>
                  {email}{" "}
                  <strong style={{ color: "#16a34a" }}>
                    — Primary Contact
                  </strong>
                </span>
              ) : (
                email
              )}
            </Checkbox>
          ))}
        </Checkbox.Group>
      ),
      okText: "Send",
      onOk: async () => {
        if (selected.length === 0) {
          message.error("Please select at least one recipient.");
          return;
        }

        try {
          const registration = registrations.find(
            (r) => r.key === contact.registrationKey,
          );
          if (!registration) throw new Error("Registration not found");

          const invoiceNumber = registration.invoiceNumber ?? "No-Invoice";

          const delegatePrice = 1950;
          const uaefmaPrice = 950;

          let basePrice = 0;
          if (contact.ticketType === "Delegate") basePrice = delegatePrice;
          else if (contact.ticketType === "UAEFMA Member")
            basePrice = uaefmaPrice;
          else if (
            contact.ticketType === "Exhibitor" &&
            registration.coupon === "Exhibitor"
          )
            basePrice = 0;

          const amount = basePrice * 1;

          const { discountedTotal, discountAmount } = await applyDiscount(
            amount,
            registration.coupon,
          );

          const pdfBytes = await generateInvoicePdf(
            {
              fullName: `${contact.fname || ""} ${contact.lname || ""}`.trim(),
              email: contact.email || "",
              mobile: contact.mobileNumber || "",
              company: contact.organization || "",
              position: contact.position || "",
            },
            invoiceNumber,
            discountedTotal.toFixed(2),
            contact.ticketType,
            1,
            basePrice,
            discountAmount,
          );

          await fetch(
            "https://us-central1-icaconference-438ba.cloudfunctions.net/sendEmailNoBCC",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                personalizations: [
                  {
                    to: selected.map((email) => ({ email })),
                    dynamic_template_data: {
                      title: contact.title || "",
                      fullName: `${contact.title || ""} ${contact.fname || ""} ${contact.lname || ""}`,
                      email: contact.email || "",
                      mobile: contact.mobileNumber || "",
                      company: contact.organization || "",
                      position: contact.position || "",
                      country: contact.country || "",
                      nationality: contact.nationality || "",
                      invoiceNumber: invoiceNumber,
                      invoiceDate: new Date().toLocaleString(),
                      companionName: contact.companionName || "",
                      companionRelationship:
                        contact.companionRelationship || "",
                    },
                  },
                ],
                attachments: [
                  {
                    content: toBase64(pdfBytes),
                    filename: `Invoice-${invoiceNumber}.pdf`,
                    type: "application/pdf",
                    disposition: "attachment",
                  },
                ],
                from: {
                  email: "registration@comms-dev.com",
                  name: "ICA Conference",
                },
                subject: "Your ICA Conference Invoice",
                template_id: "d-54a4a52ee7934670be836f8614cb4cd2",
              }),
            },
          );

          message.success("Confirmation email resent!");
        } catch (err) {
          console.error(err);
          message.error("Failed to resend email.");
        }
      },
    });
  };

  // -------------------- Invoice Download (modernized + safer) --------------------
  const handleDownloadInvoice = async (registration: Registration) => {
    try {
      const invoiceNumber = registration.invoiceNumber ?? "No-Invoice";
      const contact = registration.contactPersons?.[0];
      if (!contact) {
        message.error("No contact found for this registration.");
        return;
      }

      const fullName = getFullName(contact);

      const id = ticketIdFrom(registration.ticketId, contact.ticketType);
      if (!id) {
        message.error("Could not determine ticket type for invoice.");
        return;
      }

      const baseFee = getBaseFeeByTicketId(id) ?? 0;

      const couponCode = (registration.coupon || "").trim();
      const hasCoupon = !!couponCode && couponCode.toLowerCase() !== "none";
      const c = couponMap[couponCode.toLowerCase()];

      const round2 = (n: number) =>
        Math.round((n + Number.EPSILON) * 100) / 100;

      let finalFee = baseFee;
      let discountAmount = 0;

      if (c?.active) {
        if (c.discountType === "percentage") {
          const pct = Math.max(0, Math.min(100, c.discount));
          finalFee = baseFee * (1 - pct / 100);
          discountAmount = baseFee - finalFee;
        } else if (c.discountType === "exact") {
          // exact means flat amount OFF (same as your export logic)
          finalFee = Math.max(0, baseFee - c.discount);
          discountAmount = baseFee - finalFee;
        }
      } else if (
        hasCoupon &&
        couponCode.toLowerCase() === "exhibitor" &&
        contact.ticketType === "Exhibitor"
      ) {
        finalFee = 0;
        discountAmount = baseFee;
      }

      finalFee = round2(finalFee);
      discountAmount = round2(discountAmount);

      const pdfBytes = await generateInvoicePdf(
        {
          ...contact,
          fullName,
          company: contact.organization,
          mobile: contact.mobileNumber,
        } as any,
        invoiceNumber,
        finalFee.toFixed(2),
        contact.ticketType,
        1,
        baseFee,
        discountAmount,
      );

      if (!pdfBytes) {
        message.error("Failed to generate invoice PDF.");
        return;
      }

      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${invoiceNumber}-${contact.email}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      message.success(`Invoice ${invoiceNumber} downloaded.`);
    } catch (err) {
      console.error("handleDownloadInvoice error:", err);
      message.error("Failed to download invoice.");
    }
  };

  // -------------------- Export to Excel (styled + consistent) --------------------
  const handleExportToExcel = async () => {
    try {
      const rows: any[] = [];

      const getExportBaseFee = (ticketType: string) => {
        if (ticketType === "Delegate") return 1950;
        if (ticketType === "UAEFMA Member") return 950;
        if (ticketType === "Exhibitor") return 1950;
        return 0;
      };

      const computeDiscountedFee = async (
        ticketType: string,
        couponCode: string,
      ) => {
        const baseFee = getExportBaseFee(ticketType);

        if (couponCode === "Exhibitor" && ticketType === "Exhibitor") return 0;
        if (!couponCode || couponCode === "None") return baseFee;

        const key = couponCode.trim().toLowerCase();
        const c = couponMap[key];
        if (!c || !c.active) {
          // fallback to firestore coupon check (keeps your old behavior)
          const { discountedTotal } = await applyDiscount(baseFee, couponCode);
          return discountedTotal;
        }

        if (c.discountType === "percentage") {
          return Math.max(0, baseFee - baseFee * (c.discount / 100));
        }

        // exact = flat off
        return Math.max(0, baseFee - c.discount);
      };

      for (const r of registrations) {
        for (const p of r.contactPersons) {
          if (p.status === "Manual Entry") continue;

          const discountedFee = await computeDiscountedFee(
            p.ticketType,
            r.coupon,
          );

          rows.push({
            Date: r.timestamp,
            Title: p.title || "",
            FirstName: p.fname || "",
            LastName: p.lname || "",
            InvoiceNumber: r.invoiceNumber || "",
            Country: p.country || "",
            Nationality: p.nationality || "",
            Email: p.email || "",
            Mobile: p.mobileNumber || "",
            Institution: p.organization || "",
            Position: p.position || "",
            Fee: Number(discountedFee).toFixed(2),
            DiscountCode: r.coupon,
            Companion: toTitleCase(p.companionName || ""),
            Relationship: toTitleCase(p.companionRelationship || ""),
            Notes: p.notes || "",
            TicketType: p.ticketType,
            Status: p.status,
            BadgeType: p.badgeCategory,
          });
        }
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);

      const range = XLSX.utils.decode_range(worksheet["!ref"] || "");
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = { font: { bold: true } };
      }

      const colWidths = Object.keys(rows[0] || {}).map((key) => {
        const maxLength = Math.max(
          key.length,
          ...rows.map((row: any) => String(row[key] || "").length),
        );
        return { wch: Math.min(60, maxLength + 4) };
      });
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");

      const formattedDate = new Intl.DateTimeFormat("en-GB")
        .format(new Date())
        .replace(/\//g, "_");
      const fileName = `ICA_Participants_${formattedDate}.xlsx`;

      XLSX.writeFile(workbook, fileName, { cellStyles: true });
      message.success("Exported successfully.");
    } catch (err) {
      console.error("Export error:", err);
      message.error("Failed to export.");
    }
  };

  // -------------------- Add Manual Registration --------------------
  const handleAddRegistration = async () => {
    try {
      const ticketId =
        newContact.ticketType === "Delegate"
          ? "delegate"
          : newContact.ticketType === "UAEFMA Member"
            ? "uaefma"
            : newContact.ticketType === "Exhibitor"
              ? "exhibitor"
              : "delegate";

      const ordersRef = collection(db, "orders");
      const newOrder: any = {
        totalAmount: "0",
        totalDelegateTickets: ticketId === "delegate" ? "1" : "0",
        totalUaefmaTickets: ticketId === "uaefma" ? "1" : "0",
        coupon: "None",
        timestamp: new Date(),
        invoiceNumber: "",
        ticketId,
      };

      const orderDoc = await addDoc(ordersRef, newOrder);

      const contactsRef = collection(db, `orders/${orderDoc.id}/contacts`);
      await addDoc(contactsRef, {
        fname: newContact.fname || "",
        lname: newContact.lname || "",
        organization: newContact.organization || "",
        position: newContact.position || "",
        mobileNumber: newContact.mobileNumber || "",
        country: newContact.country || "",
        nationality: newContact.nationality || "",
        title: newContact.title || "",
        email: newContact.email || "",
        ticketId,
        badgeCategory: newContact.badgeCategory || "Delegate",
        badgeCollected: "No",
        status: "Manual Entry",
        notes: "",
        companion: {
          fullName: newContact.companionName || "",
          relationship: newContact.companionRelationship || "",
        },
      });

      message.success("New registration added.");

      setNewContact({
        fname: "",
        lname: "",
        email: "",
        organization: "",
        position: "",
        country: "",
        nationality: "",
        mobileNumber: "",
        companionName: "",
        companionRelationship: "",
        badgeCategory: "Delegate",
        ticketType: "Delegate",
        title: "",
      });

      setIsAddModalVisible(false);
      fetchRegistrations();
    } catch (err) {
      console.error("Add registration error:", err);
      message.error("Failed to add registration.");
    }
  };

  // -------------------- Flatten + Filter --------------------
  const filteredData = useMemo(() => {
    const flat = registrations.flatMap((r) =>
      r.contactPersons.map((p) => ({
        ...p,
        timestamp: r.timestamp,
        invoiceNumber: r.invoiceNumber,
        registrationKey: r.key,
        coupon: r.coupon,
      })),
    );

    return flat.filter((item: any) => {
      const text =
        `${item.fname} ${item.lname} ${item.email} ${item.organization} ${item.country} ${item.invoiceNumber}`.toLowerCase();
      if (searchTerm && !text.includes(searchTerm)) return false;

      if (filterCategory && selectedValues.length > 0) {
        const val = (item[filterCategory] || "N/A").toString();
        return selectedValues.includes(val);
      }
      return true;
    });
  }, [registrations, searchTerm, filterCategory, selectedValues]);

  const counts = useMemo(() => {
    const all = filteredData.length;
    const manual = filteredData.filter(
      (d: any) => d.status === "Manual Entry",
    ).length;
    const normal = all - manual;
    return { all, manual, normal };
  }, [filteredData]);

  // -------------------- Columns --------------------
  const orderColumns: any = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      sorter: (a: any, b: any) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8, width: 260 }}>
          <RangePicker
            allowClear
            onChange={(dates) => setSelectedKeys(dates ? [dates] : [])}
            onOk={() => confirm()}
            style={{ marginBottom: 8, display: "block", width: "100%" }}
          />
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
            style={{ width: "100%" }}
          >
            Reset
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) => {
        if (!value?.length) return true;
        const [start, end] = value;
        const recDate = new Date(record.timestamp);
        return (
          recDate >= start.startOf("day").toDate() &&
          recDate <= end.endOf("day").toDate()
        );
      },
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 180,
      render: (invoiceNumber: string, record: any) => {
        const isManual = record.status === "Manual Entry";
        return (
          <Space size={8}>
            {isManual ? (
              <Tag color="orange">MANUAL ENTRY</Tag>
            ) : (
              <Text style={{ fontWeight: 600 }}>{invoiceNumber || "—"}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Badge Category",
      key: "badgeCategory",
      width: 220,
      render: (_: any, contact: any) => (
        <Select
          value={contact.badgeCategory || ""}
          placeholder="Select category"
          style={{ width: "100%" }}
          onChange={async (value) => {
            setRegistrations((prevRegs) =>
              prevRegs.map((reg) =>
                reg.key === contact.registrationKey
                  ? {
                      ...reg,
                      contactPersons: reg.contactPersons.map((c) =>
                        c.docId === contact.docId
                          ? { ...c, badgeCategory: value }
                          : c,
                      ),
                    }
                  : reg,
              ),
            );
            await updateContactField(
              contact.registrationKey,
              contact.docId,
              "badgeCategory",
              value,
            );
          }}
          options={[
            "Delegate",
            "Exhibitor",
            "Speaker",
            "Moderator",
            "VIP",
            "ICA Executive",
            "Sponsor",
            "ICA Honorary",
            "Guest",
            "Organizer",
            "Media",
            "Press",
            "Security",
            "Hostess",
            "Support Crew",
            "Companion",
            "UAEFMA Board Member",
          ].map((v) => ({
            value: v,
            label: v === "ICA Executive" ? "ICA Executive Committee" : v,
          }))}
        />
      ),
    },
    {
      title: "Full Name",
      key: "fullName",
      width: 220,
      render: (_: any, r: any) => (
        <Text style={{ fontWeight: 600 }}>{getFullName(r)}</Text>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 220 }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small">
              Search
            </Button>
            <Button onClick={() => clearFilters?.()} size="small">
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value: any, record: any) => {
        const name = `${record.fname} ${record.lname}`.toLowerCase();
        return name.includes(String(value || "").toLowerCase());
      },
    },
    { title: "Email", dataIndex: "email", key: "email", width: 220 },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 160,
    },
    {
      title: "Organization",
      dataIndex: "organization",
      key: "organization",
      width: 220,
    },
    { title: "Position", dataIndex: "position", key: "position", width: 180 },
    {
      title: "Ticket Type",
      dataIndex: "ticketType",
      key: "ticketType",
      width: 160,
      filters: [
        { text: "Delegate", value: "Delegate" },
        { text: "UAEFMA Member", value: "UAEFMA Member" },
        { text: "Exhibitor", value: "Exhibitor" },
      ],
      onFilter: (value: any, record: any) => record.ticketType === value,
    },
    { title: "Country", dataIndex: "country", key: "country", width: 160 },
    {
      title: "Nationality",
      dataIndex: "nationality",
      key: "nationality",
      width: 160,
    },
    {
      title: "Companion Name",
      dataIndex: "companionName",
      key: "companionName",
      width: 180,
    },
    {
      title: "Relationship",
      dataIndex: "companionRelationship",
      key: "companionRelationship",
      width: 180,
    },
    {
      title: "How Did You Hear About Us?",
      key: "heard",
      width: 260,
      render: (_: any, p: Participants) => {
        const src = p.referralSource || "";
        const other = p.referralOther || "";
        return src ? (other ? `${src} — ${other}` : src) : "—";
      },
      filters: [
        { text: "Social Media", value: "Social Media" },
        { text: "Google/Search", value: "Google/Search" },
        { text: "Colleague/Word of Mouth", value: "Colleague/Word of Mouth" },
        { text: "Association", value: "Association" },
        { text: "Other (specified)", value: "__HAS_OTHER__" },
      ],
      onFilter: (value: any, record: any) => {
        if (value === "__HAS_OTHER__") {
          return Boolean(
            record.referralOther && String(record.referralOther).trim(),
          );
        }
        const src = (record.referralSource || "").toLowerCase();
        return src.includes(String(value).toLowerCase());
      },
    },
    {
      title: "Status",
      key: "status",
      width: 320,
      render: (_: any, contact: any) => (
        <Space style={{ width: "100%" }} size={8}>
          <Tag
            color={statusToTagColor(contact.status)}
            style={{ borderRadius: 999, margin: 0 }}
          >
            {contact.status || "Awaiting Payment Confirmation"}
          </Tag>
          <Select
            value={contact.status || "Awaiting Payment Confirmation"}
            style={{ flex: 1 }}
            onChange={(value) => {
              // local update
              setRegistrations((prevRegs) =>
                prevRegs.map((reg) =>
                  reg.key === contact.registrationKey
                    ? {
                        ...reg,
                        contactPersons: reg.contactPersons.map((c) =>
                          c.docId === contact.docId
                            ? { ...c, status: value }
                            : c,
                        ),
                      }
                    : reg,
                ),
              );

              debounceUpdateStatus(
                contact.registrationKey,
                contact.docId,
                value,
              );
            }}
            options={[
              {
                value: "Awaiting Payment Confirmation",
                label: "Awaiting Payment Confirmation",
              },
              { value: "Paid", label: "Paid" },
              { value: "Pending Payment", label: "Pending Payment" },
              {
                value: "Payment Not Reflected",
                label: "Payment Not Reflected",
              },
              { value: "Complimentary Pass", label: "Complimentary Pass" },
              { value: "Manual Entry", label: "Manual Entry" },
            ]}
          />
        </Space>
      ),
    },
    {
      title: "Notes",
      key: "notes",
      width: 280,
      render: (_: any, contact: any) => (
        <TextArea
          rows={2}
          value={contact.notes || ""}
          onChange={(e) => {
            const newNote = e.target.value;

            // local update
            setRegistrations((prevRegs) =>
              prevRegs.map((reg) => {
                if (reg.key !== contact.registrationKey) return reg;
                const updatedContacts = reg.contactPersons.map((c) =>
                  c.docId === contact.docId ? { ...c, notes: newNote } : c,
                );
                return { ...reg, contactPersons: updatedContacts };
              }),
            );

            debounceUpdateNotes(
              contact.registrationKey,
              contact.docId,
              newNote,
            );
          }}
          placeholder="Enter payment reference or notes"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: isMobile ? undefined : "right",
      width: 220,
      render: (_: any, contact: ParticipantsWithOrder) => (
        <Space wrap>
          <Button
            icon={<FaPen />}
            onClick={() => {
              setEditingContact(contact);
              setEditModalVisible(true);
            }}
            style={{ borderRadius: 12 }}
          >
            Edit
          </Button>

          <Button
            icon={<FaFileDownload />}
            onClick={() => {
              const reg = registrations.find(
                (r) => r.key === contact.registrationKey,
              );
              if (reg) handleDownloadInvoice(reg);
            }}
            style={{ borderRadius: 12 }}
          >
            Invoice
          </Button>

          <Button
            type="link"
            onClick={() => handleResendRegistrationEmail(contact)}
          >
            Resend Email
          </Button>
        </Space>
      ),
    },
  ];

  // -------------------- UI --------------------
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Toolbar Card */}
      <Card style={glassCard} bodyStyle={{ padding: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* Filters Row */}
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Input
              className="toolbar-control"
              prefix={<SearchOutlined />}
              allowClear
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: isMobile ? "100%" : 420 }}
            />

            <Space wrap>
              <Select
                className="toolbar-control"
                placeholder="Filter by"
                value={filterCategory}
                style={{ width: 220 }}
                allowClear
                suffixIcon={<FilterOutlined />}
                onChange={(val) => {
                  setFilterCategory(val);
                  setSelectedValues([]);
                }}
                options={[
                  { label: "Institution", value: "organization" },
                  { label: "Country", value: "country" },
                  { label: "Nationality", value: "nationality" },
                  { label: "Ticket Type", value: "ticketType" },
                  { label: "Badge Type", value: "badgeCategory" },
                  { label: "Status", value: "status" },
                ]}
              />

              {filterCategory && (
                <Select
                  mode="multiple"
                  allowClear
                  value={selectedValues}
                  onChange={(vals) => setSelectedValues(vals)}
                  style={{ minWidth: isMobile ? 260 : 360 }}
                  options={Array.from(
                    new Set(
                      registrations.flatMap((r) =>
                        r.contactPersons.map(
                          (p: any) => p[filterCategory] || "N/A",
                        ),
                      ),
                    ),
                  )
                    .sort()
                    .map((v) => ({ label: v, value: v }))}
                />
              )}

              <Button
                className="toolbar-control toolbar-btn"
                onClick={() => {
                  setFilterCategory(null);
                  setSelectedValues([]);
                  setSearchTerm("");
                }}
                style={controlStyle}
              >
                Clear
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Tooltip title="Export participants to Excel">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                style={{ ...controlStyle, fontWeight: 600 }}
              >
                Export Excel
              </Button>
            </Tooltip>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModalVisible(true)}
              style={{ ...controlStyle, fontWeight: 700 }}
            >
              Add Delegate
            </Button>
          </Space>
        </div>
      </Card>

      {/* Table Card */}
      <Card style={glassCard} bodyStyle={{ padding: 12 }}>
        <Tabs
          defaultActiveKey="all"
          items={[
            {
              key: "all",
              label: (
                <Space>
                  <span>Online Registrations</span>
                  <Tag style={{ borderRadius: 999, margin: 0 }}>
                    {counts.normal}
                  </Tag>
                </Space>
              ),
              children: (
                <Table
                  columns={orderColumns}
                  dataSource={filteredData.filter(
                    (d: any) => d.status !== "Manual Entry",
                  )}
                  rowKey={(record: any) =>
                    `${record.registrationKey}-${record.email}`
                  }
                  pagination={{
                    current: currentPage,
                    pageSize,
                    onChange: (page) => setCurrentPage(page),
                  }}
                  bordered={false}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  rowClassName={() => "modern-row"}
                />
              ),
            },
            {
              key: "manual",
              label: (
                <Space>
                  <span>Manual Registrations</span>
                  <Tag color="orange" style={{ borderRadius: 999, margin: 0 }}>
                    {counts.manual}
                  </Tag>
                </Space>
              ),
              children: (
                <Table
                  columns={orderColumns}
                  dataSource={filteredData.filter(
                    (d: any) => d.status === "Manual Entry",
                  )}
                  rowKey={(record: any) =>
                    `${record.registrationKey}-${record.email}`
                  }
                  pagination={{
                    current: currentPage,
                    pageSize,
                    onChange: (page) => setCurrentPage(page),
                  }}
                  bordered={false}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  rowClassName={() => "modern-row"}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Print Badges Modal */}
      <Modal
        title="Print Badges"
        open={isPrintModalVisible}
        onCancel={() => setIsPrintModalVisible(false)}
        footer={
          <Button
            type="primary"
            onClick={handlePrintSelected}
            disabled={selectedContacts.length === 0}
          >
            {selectedContacts.length > 1
              ? `Print Selected (${selectedContacts.length})`
              : `Print Selected`}
          </Button>
        }
      >
        <Table
          columns={modalColumns}
          dataSource={currentContacts}
          pagination={{ pageSize: 5 }}
          bordered
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Registration"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={async () => {
          if (!editingContact) return;
          try {
            const contactRef = doc(
              db,
              `orders/${editingContact.registrationKey}/contacts`,
              editingContact.docId!,
            );

            await updateDoc(contactRef, {
              fname: editingContact.fname || "",
              lname: editingContact.lname || "",
              organization: editingContact.organization || "",
              position: editingContact.position || "",
              mobileNumber: editingContact.mobileNumber || "",
              country: editingContact.country || "",
              nationality: editingContact.nationality || "",
              companion: {
                fullName: editingContact.companionName || "",
                relationship: editingContact.companionRelationship || "",
              },
            });

            message.success("Updated successfully.");
            setEditModalVisible(false);
            fetchRegistrations();
          } catch (err) {
            console.error(err);
            message.error("Failed to update contact");
          }
        }}
      >
        <div className="edit-modal-container">
          {[
            { label: "First Name", key: "fname" as const },
            { label: "Last Name", key: "lname" as const },
            { label: "Organization", key: "organization" as const },
            { label: "Position", key: "position" as const },
            { label: "Mobile Number", key: "mobileNumber" as const },
            { label: "Country", key: "country" as const },
            { label: "Nationality", key: "nationality" as const },
            { label: "Companion", key: "companionName" as const },
            { label: "Relationship", key: "companionRelationship" as const },
          ].map((f) => (
            <div className="edit-modal-field" key={f.key}>
              <label>{f.label}</label>
              <input
                value={(editingContact as any)?.[f.key] || ""}
                onChange={(e) =>
                  setEditingContact((prev) =>
                    prev ? { ...prev, [f.key]: e.target.value } : prev,
                  )
                }
              />
            </div>
          ))}
        </div>
      </Modal>

      {/* Add Manual Registration */}
      <Modal
        title="Add New Registration"
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onOk={handleAddRegistration}
        okText="Save"
      >
        <div
          className="edit-modal-container"
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div>
            <label style={{ fontWeight: 600 }}>Title</label>
            <Input
              value={newContact.title || ""}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., Mr / Ms / Dr"
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>First Name</label>
            <Input
              value={newContact.fname || ""}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, fname: e.target.value }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Last Name</label>
            <Input
              value={newContact.lname || ""}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, lname: e.target.value }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Email Address</label>
            <Input
              type="email"
              value={newContact.email || ""}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, email: e.target.value }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Ticket Type</label>
            <Select
              value={newContact.ticketType || "Delegate"}
              onChange={(val) =>
                setNewContact((prev) => ({ ...prev, ticketType: val }))
              }
              style={{ width: "100%", ...controlStyle }}
              options={[
                { label: "Delegate", value: "Delegate" },
                { label: "UAEFMA Member", value: "UAEFMA Member" },
                { label: "Exhibitor", value: "Exhibitor" },
              ]}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Organization</label>
            <Input
              value={newContact.organization || ""}
              onChange={(e) =>
                setNewContact((prev) => ({
                  ...prev,
                  organization: e.target.value,
                }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Position</label>
            <Input
              value={newContact.position || ""}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, position: e.target.value }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Country</label>
            <CustomCountrySelect
              value={newContact.country || ""}
              onChange={(val) =>
                setNewContact((prev) => ({ ...prev, country: val }))
              }
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Nationality</label>
            <CustomCountrySelect
              value={newContact.nationality || ""}
              onChange={(val) =>
                setNewContact((prev) => ({ ...prev, nationality: val }))
              }
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Mobile Number</label>
            <div className="phone-input-wrapper">
              <PhoneInput
                value={newContact.mobileNumber || ""}
                onChange={(val) =>
                  setNewContact((prev) => ({
                    ...prev,
                    mobileNumber: val || "",
                  }))
                }
                defaultCountry="AE"
                international
              />
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Companion Name (optional)</label>
            <Input
              value={newContact.companionName || ""}
              onChange={(e) =>
                setNewContact((prev) => ({
                  ...prev,
                  companionName: e.target.value,
                }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Relationship (optional)</label>
            <Input
              value={newContact.companionRelationship || ""}
              onChange={(e) =>
                setNewContact((prev) => ({
                  ...prev,
                  companionRelationship: e.target.value,
                }))
              }
              style={controlStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: 600 }}>Badge Category</label>
            <Select
              value={newContact.badgeCategory || "Delegate"}
              onChange={(val) =>
                setNewContact((prev) => ({ ...prev, badgeCategory: val }))
              }
              style={{ width: "100%", ...controlStyle }}
              options={[
                "Delegate",
                "Exhibitor",
                "Speaker",
                "Moderator",
                "VIP",
                "ICA Executive",
                "Sponsor",
                "ICA Honorary",
                "Organizer",
                "Media",
                "Press",
                "Companion",
                "Guest",
                "Security",
                "UAEFMA Board Member",
              ].map((v) => ({
                value: v,
                label: v === "ICA Executive" ? "ICA Executive Committee" : v,
              }))}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManageRegistrationsV2;
